import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../../../services/api";
import { API_URL } from "@/constants";
import type { AnalyticsVersion } from "types/";
import {
  ANALYTICS_STALE_TIME,
  PREVIEW_VERSION,
  VERSION_PARAM,
} from "../../components/analytics/constants/constants";

/**
 * The version the backend reads when none is sent: the latest live one,
 * else the latest publication, else preview.
 */
export const defaultVersion = (versions: AnalyticsVersion[]): string => {
  const live = versions.find((v) => v.is_published);
  const published = versions.find(
    (v) => v.project_publication_id !== PREVIEW_VERSION
  );
  return (live ?? published)?.project_publication_id ?? PREVIEW_VERSION;
};

export type AnalyticsVersionState = ReturnType<typeof useAnalyticsVersion>;

/**
 * The versions viewers saw (latest first) and the one picked in the URL
 * (`?version=<id|preview|all>`), shared by the analytics tabs.
 */
export function useAnalyticsVersion(projectId: string | undefined) {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useQuery({
    queryKey: ["analytics", projectId, "versions"],
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<AnalyticsVersion[]>(
        `${API_URL}/analytics/projects/${projectId}/analytics/versions`,
        { signal }
      );
      return data;
    },
    enabled: !!projectId,
    staleTime: ANALYTICS_STALE_TIME,
  });
  const versions = query.data ?? [];
  // With a single version, it and "all versions" read the same figures.
  const hasChoice = versions.length > 1;

  // Without a pick, nothing is sent and the backend serves its default, so the
  // data doesn't wait for the version list. On a failed list the select is
  // hidden, so a URL pick is dropped rather than filtering out of sight.
  const requested = query.isError
    ? undefined
    : (searchParams.get(VERSION_PARAM) ?? undefined);
  const selected =
    requested ?? (query.data ? defaultVersion(query.data) : undefined);

  const setVersion = useCallback(
    (version: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(VERSION_PARAM, version);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return {
    versions,
    /** The version to send to the API: undefined lets the backend pick. */
    requested,
    /** The version to show as picked, once known. */
    selected,
    /** Whether viewers saw more than one version, so picking one changes the figures. */
    hasChoice,
    setVersion,
    isLoading: query.isLoading && !!projectId,
  };
}
